import { useState } from 'react';
import { Sparkles, Loader2, Check, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const VIBES = [
  { id: 'warm', label: 'Warm & Personal', desc: 'Like a hug from your best friend', emoji: 'cozy' },
  { id: 'elegant', label: 'Elegant & Refined', desc: 'Think Vogue, not Pinterest', emoji: 'luxe' },
  { id: 'playful', label: 'Playful & Fun', desc: 'Life is short, weddings should be fun', emoji: 'spark' },
  { id: 'raw', label: 'Raw & Honest', desc: 'No fluff, just real moments', emoji: 'real' },
  { id: 'cinematic', label: 'Cinematic & Moody', desc: 'Every frame tells a story', emoji: 'film' },
  { id: 'minimal', label: 'Minimal & Clean', desc: 'Let the work speak for itself', emoji: 'zen' },
];

const CopyQuestionnaire = ({ siteId, siteName, onClose, onSiteUpdated }) => {
  const [step, setStep] = useState(0); // 0=vibe, 1=personality, 2=client, 3=differentiator, 4=rewriting, 5=done
  const [vibe, setVibe] = useState('');
  const [personality, setPersonality] = useState('');
  const [dreamClient, setDreamClient] = useState('');
  const [differentiator, setDifferentiator] = useState('');
  const [voiceType, setVoiceType] = useState('solo');
  const [error, setError] = useState('');

  const handleRewrite = async () => {
    setStep(4);
    setError('');
    try {
      await axios.post(`${API}/seo/copy-rewrite`, {
        site_id: siteId, vibe,
        personality: personality + ` IMPORTANT: Use "${voiceType === 'team' ? 'we/us/our' : 'I/me/my'}" pronouns throughout ALL copy.`,
        dream_client: dreamClient, differentiator,
      }, { withCredentials: true, timeout: 120000 });
      setStep(5);
      if (onSiteUpdated) onSiteUpdated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
      setStep(3);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!vibe;
    if (step === 1) return personality.trim().length > 10;
    if (step === 2) return dreamClient.trim().length > 5;
    if (step === 3) return differentiator.trim().length > 5;
    return true;
  };

  const next = () => {
    if (step === 3) { handleRewrite(); return; }
    setStep(s => s + 1);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="copy-questionnaire" style={{ background: 'var(--bg)', border: '1px solid var(--faint)', maxWidth: '520px', width: '95%', padding: '40px', overflow: 'auto', maxHeight: '85vh', borderRadius: '12px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <p style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '4px' }}>
              {step <= 3 ? `Step ${step + 1} of 4` : ''}
            </p>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '22px' }}>
              {step === 0 && 'What vibe are you going for?'}
              {step === 1 && 'Tell Iris about yourself.'}
              {step === 2 && 'Describe your dream client.'}
              {step === 3 && 'What makes you different?'}
              {step === 4 && 'Rewriting your site...'}
              {step === 5 && 'Your site sounds like you now.'}
            </h2>
          </div>
          {step < 4 && <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '18px' }}>&times;</button>}
        </div>

        {error && <p style={{ color: '#e88', fontSize: '13px', padding: '10px', background: 'rgba(196,72,72,0.1)', border: '1px solid rgba(196,72,72,0.2)', marginBottom: '16px' }}>{error}</p>}

        {/* STEP 0: Vibe selection */}
        {step === 0 && (
          <div data-testid="copy-step-vibe">
            <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '20px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              Pick the voice that feels most like you. This shapes ALL the copy on your site.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {VIBES.map(v => (
                <button key={v.id} onClick={() => setVibe(v.id)} data-testid={`vibe-${v.id}`} style={{
                  padding: '16px', textAlign: 'left', border: `1.5px solid ${vibe === v.id ? 'var(--gold)' : 'var(--faint)'}`,
                  background: vibe === v.id ? 'rgba(201,168,76,0.06)' : 'transparent',
                  cursor: 'pointer', transition: 'all .15s',
                }}>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, color: vibe === v.id ? 'var(--gold)' : 'var(--fg)', marginBottom: '3px' }}>{v.label}</p>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: 'var(--stone)', fontWeight: 300 }}>{v.desc}</p>
                </button>
              ))}
            </div>

            {/* Solo vs Team */}
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--stone)', marginBottom: '8px' }}>How should your site talk about you?</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { key: 'solo', label: "Just me", sub: '"I" and "my"' },
                  { key: 'team', label: "We're a team", sub: '"we" and "our"' },
                ].map(v => (
                  <button key={v.key} onClick={() => setVoiceType(v.key)} style={{
                    flex: 1, padding: '12px', textAlign: 'center', borderRadius: '10px',
                    background: voiceType === v.key ? 'rgba(201,168,76,0.06)' : 'transparent',
                    border: `1.5px solid ${voiceType === v.key ? 'var(--gold)' : 'var(--faint)'}`,
                    cursor: 'pointer', transition: 'all .15s',
                  }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 500, fontFamily: 'var(--sans)', color: voiceType === v.key ? 'var(--gold)' : 'var(--fg)' }}>{v.label}</span>
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--stone)', fontFamily: 'var(--sans)' }}>{v.sub}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Personality */}
        {step === 1 && (
          <div data-testid="copy-step-personality">
            <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '16px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              How would you introduce yourself at a wedding expo? Be yourself — Iris will write in your voice.
            </p>
            <textarea
              value={personality}
              onChange={e => setPersonality(e.target.value)}
              placeholder="e.g. We're a husband-wife team who met in film school. We cry at every ceremony and our dog sometimes comes to shoots. We believe the best photos happen when nobody's posing."
              data-testid="copy-input-personality"
              style={{ width: '100%', padding: '14px', fontSize: '14px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', outline: 'none', resize: 'vertical', minHeight: '120px', lineHeight: 1.6 }}
            />
          </div>
        )}

        {/* STEP 2: Dream client */}
        {step === 2 && (
          <div data-testid="copy-step-client">
            <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '16px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              Who do you love working with? The more specific, the better your copy will attract them.
            </p>
            <textarea
              value={dreamClient}
              onChange={e => setDreamClient(e.target.value)}
              placeholder="e.g. Couples who want their wedding to feel like a party, not a production. They care more about moments than poses. They probably found us on Instagram and love our moody, candid style."
              data-testid="copy-input-client"
              style={{ width: '100%', padding: '14px', fontSize: '14px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', outline: 'none', resize: 'vertical', minHeight: '120px', lineHeight: 1.6 }}
            />
          </div>
        )}

        {/* STEP 3: Differentiator */}
        {step === 3 && (
          <div data-testid="copy-step-diff">
            <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '16px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              What would a past client say makes you different from other photographers?
            </p>
            <textarea
              value={differentiator}
              onChange={e => setDifferentiator(e.target.value)}
              placeholder="e.g. We deliver a cinematic highlight film the next day. Couples always say they forgot we were even there. Our editing style looks like a movie, not a slideshow."
              data-testid="copy-input-diff"
              style={{ width: '100%', padding: '14px', fontSize: '14px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', outline: 'none', resize: 'vertical', minHeight: '120px', lineHeight: 1.6 }}
            />
          </div>
        )}

        {/* STEP 4: Rewriting */}
        {step === 4 && (
          <div data-testid="copy-rewriting" style={{ textAlign: 'center', padding: '32px 0' }}>
            <Loader2 size={28} className="animate-spin" style={{ color: 'var(--gold)', margin: '0 auto 16px' }} />
            <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', color: 'var(--fg)', marginBottom: '8px' }}>Iris is rewriting your entire site...</p>
            <p style={{ color: 'var(--stone)', fontSize: '13px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              Headlines, about section, testimonials, CTAs — everything is getting your voice.
            </p>
          </div>
        )}

        {/* STEP 5: Done */}
        {step === 5 && (
          <div data-testid="copy-done" style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(91,168,157,0.1)', border: '1px solid rgba(91,168,157,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={24} style={{ color: '#5ba89d' }} />
            </div>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', color: 'var(--fg)', marginBottom: '8px' }}>
              Every word on your site now sounds like you.
            </p>
            <p style={{ color: 'var(--stone)', fontSize: '13px', marginBottom: '28px', fontFamily: 'var(--sans)', fontWeight: 300 }}>
              Check the preview — if anything feels off, ask Iris in the chat to tweak it.
            </p>
            <button onClick={onClose} className="btn-accent" data-testid="copy-done-btn" style={{ padding: '12px 32px', fontSize: '12px' }}>See My Site</button>
          </div>
        )}

        {/* Navigation buttons */}
        {step <= 3 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} style={{ flex: 0, padding: '12px 20px', fontFamily: 'var(--sans)', fontSize: '12px', background: 'none', border: '1px solid var(--faint)', color: 'var(--stone)', cursor: 'pointer', borderRadius: '4px' }}>
                Back
              </button>
            )}
            <button onClick={next} disabled={!canProceed()} data-testid="copy-next-btn" style={{
              flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 500,
              background: canProceed() ? 'var(--gold)' : 'var(--faint)', color: canProceed() ? '#000' : 'var(--muted)',
              border: 'none', cursor: canProceed() ? 'pointer' : 'default', borderRadius: '4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all .15s',
            }}>
              {step === 3 ? <><Sparkles size={14} /> Rewrite My Copy</> : <>Next <ChevronRight size={14} /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CopyQuestionnaire;
